/**
 * Color utilities for deriving shades from a base color
 * Used for generating danger/error color palettes from tenant-configured danger color
 */

/**
 * Parse hex color to RGB components
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) throw new Error(`Invalid hex color: ${hex}`);
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/**
 * Convert RGB to hex
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}

/**
 * Lighten a color by mixing with white
 * @param hex - Base hex color
 * @param amount - Amount to lighten (0-1, where 1 is white)
 */
export function lightenColor(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
}

/**
 * Darken a color by mixing with black
 * @param hex - Base hex color
 * @param amount - Amount to darken (0-1, where 1 is black)
 */
export function darkenColor(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

/**
 * Generate danger color palette from a base danger color
 * Returns values for all error/danger CSS variables in both light and dark modes
 */
export function generateDangerPalette(dangerColor: string): {
  errorColor: string;
  alertErrorBg: string;
  alertErrorBorder: string;
  alertErrorText: string;
  alertErrorBgDark: string;
  alertErrorBorderDark: string;
  alertErrorTextDark: string;
} {
  return {
    // Light mode
    errorColor: dangerColor,
    alertErrorBg: lightenColor(dangerColor, 0.9), // Very light background
    alertErrorBorder: dangerColor, // Same as main color
    alertErrorText: darkenColor(dangerColor, 0.4), // Darker for text readability
    // Dark mode
    alertErrorBgDark: darkenColor(dangerColor, 0.85), // Very dark background
    alertErrorBorderDark: darkenColor(dangerColor, 0.1), // Slightly darker for dark mode
    alertErrorTextDark: lightenColor(dangerColor, 0.5), // Lighter for dark bg
  };
}

/**
 * Calculate relative luminance of a color using WCAG 2.1 formula
 * @param hex - Hex color string (e.g., '#00233c')
 * @returns Luminance value between 0 (black) and 1 (white)
 */
export function getLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Check if a color is considered "dark" (low luminance)
 * Used to determine if colors need lightening for dark mode visibility
 * @param hex - Hex color string
 * @param threshold - Luminance threshold (default 0.3, colors below this are "dark")
 */
export function isColorDark(hex: string, threshold = 0.3): boolean {
  return getLuminance(hex) < threshold;
}

/**
 * Convert RGB to HSL
 * @returns h in [0,360), s and l in [0,1]
 */
export function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    // Achromatic (grayscale)
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
  }

  return { h: h * 360, s, l };
}

/**
 * Convert HSL to RGB
 * @param h - Hue in [0,360)
 * @param s - Saturation in [0,1]
 * @param l - Lightness in [0,1]
 */
export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h = ((h % 360) + 360) % 360; // Normalize hue to [0,360)
  h /= 360;

  if (s === 0) {
    // Achromatic (grayscale)
    const gray = Math.round(l * 255);
    return { r: gray, g: gray, b: gray };
  }

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  };
}

/**
 * Convert hex color to HSL
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

/**
 * Convert HSL to hex color
 */
export function hslToHex(h: number, s: number, l: number): string {
  const { r, g, b } = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
}

/**
 * Transform a dark color into a vibrant "neon" version for dark mode.
 * Unlike lightenColor (which mixes with white and desaturates), this preserves
 * the color's hue and ensures high saturation for a vibrant appearance.
 *
 * @param hex - Base hex color to transform
 * @param targetLightness - Target lightness value (0-1, default 0.55)
 * @param minSaturation - Minimum saturation to ensure vibrancy (0-1, default 0.7)
 * @returns Transformed hex color
 *
 * @example
 * neonifyColor('#00233c') // Dark navy → vibrant blue (#0080db)
 */
export function neonifyColor(hex: string, targetLightness = 0.55, minSaturation = 0.7): string {
  const { h, s } = hexToHsl(hex);

  // For grayscale/near-grayscale colors (saturation < 5%), just lighten without adding saturation
  // This prevents grayscale from being tinted (e.g., black turning red)
  if (s < 0.05) {
    return hslToHex(h, s, targetLightness);
  }

  // Keep the hue, boost saturation if needed, set target lightness
  const newSaturation = Math.max(s, minSaturation);

  return hslToHex(h, newSaturation, targetLightness);
}
